import { weekDaysObject } from "@data/constants/activitiesConstants"
import moment from "moment"
import "moment/locale/fr"
import {
    chipLabelOf,
    frTime,
    slotDayOf,
    slotKeyOf,
    slotRangeLabel,
    slotStartOf,
} from "./maDispoSlot"

/**
 * Pure logic of « Libérer des créneaux »: what the tutor's draft posts, and what
 * each line of the read-only preview says. The rules themselves live on the
 * server — this only translates its verdicts into the tutor's words.
 */

export const RELEASE_MOTIF = {
    SCHEDULE_CHANGE: "schedule_change",
    UNAVAILABLE: "unavailable",
}

export const STUDENT_OUTCOME = {
    KEPT: "kept",
    NEW_GROUP: "new_group",
    REATTRIBUTION: "reattribution",
}

/** What the server decided for a stage on an hour he gives up (`stages[].outcome`). */
export const STAGE_OUTCOME = {
    COVERED: "stage_covered",
    REATTRIBUTED: "stage_reattributed",
}

const dayOf = (slotString) => {
    const day = slotDayOf(slotString)
    return (weekDaysObject[day] ?? day).toLowerCase()
}

/** "Tuesday 18:00" → "mardi à 18h" — a weekly créneau, never timezone-converted. */
export const dayTimeLabel = (slotString) =>
    `${dayOf(slotString)} à ${frTime(slotKeyOf(slotString).split(" ")[1])}`

const parseLocal = (dateTime) => moment(dateTime, "YYYY-MM-DD HH:mm").locale("fr")

/** "2026-10-21 18:00" → "mardi 21/10". */
export const dayDateLabel = (dateTime) => parseLocal(dateTime).format("dddd DD/MM")

/** "2026-10-21 18:00" → "mardi 21/10 à 18h". */
export const dayDateTimeLabel = (dateTime) => {
    const m = parseLocal(dateTime)
    return `${m.format("dddd DD/MM")} à ${frTime(m.format("HH:mm"))}`
}

/** "2026-10-20" → "20/10". */
export const shortDateLabel = (date) => moment(date, "YYYY-MM-DD").format("DD/MM")

/** The engaged créneaux of a week view still held — the only ones this flow can free. */
export const engagedSlotsOf = (weekView) =>
    (weekView?.slots ?? []).filter((s) => s.engaged && !s.released)

/** The engaged créneau that holds this live, if he still holds it — matched on the live, never on a clock. */
export const engagedSlotOfLive = (engagedSlots, liveSessionId) =>
    liveSessionId == null
        ? null
        : engagedSlots.find((s) => (s.live_session_ids ?? []).map(Number).includes(Number(liveSessionId))) ?? null

/** The créneaux an earlier gesture already freed: their live runs until it ends. */
export const releasedSlotsOf = (weekView) =>
    (weekView?.slots ?? []).filter((s) => s.engaged && s.released)

/** The open (declared, live-free) créneaux of a week view. */
export const openSlotsOf = (weekView) =>
    (weekView?.slots ?? []).filter((s) => !s.engaged).map((s) => s.slot)

/**
 * An engaged créneau as the tutor knows it — by its live: "Mardi 18h30" for a
 * live at 18h30 folded onto the 18h cell, "Mardi 18h-19h" otherwise.
 */
export const engagedSlotLabel = (weekSlot) => {
    const day = slotDayOf(weekSlot.slot)
    return `${weekDaysObject[day] ?? day} ${chipLabelOf({
        engaged: true,
        time: slotKeyOf(weekSlot.slot).split(" ")[1],
        label: slotRangeLabel(weekSlot.slot),
        liveTimes: weekSlot.live_times,
    })}`
}

/** The open créneaux with one more, never twice the same hour. */
export const withSlot = (openSlots, slot) =>
    openSlots.some((s) => slotKeyOf(s) === slotKeyOf(slot)) ? openSlots : [...openSlots, slot]

/** What the week draft changes on the open créneaux, keyed like the server. */
export const weekDiff = (serverOpenSlots, draftOpenSlots) => {
    const serverKeys = new Set(serverOpenSlots.map(slotKeyOf))
    const draftKeys = new Set(draftOpenSlots.map(slotKeyOf))

    return {
        new_slots: draftOpenSlots
            .filter((s) => !serverKeys.has(slotKeyOf(s)))
            .map(slotStartOf),
        dropped_slots: serverOpenSlots
            .filter((s) => !draftKeys.has(slotKeyOf(s)))
            .map(slotStartOf),
    }
}

/**
 * The body shared by preview and apply. Stopping altogether frees every créneau
 * and leaves no week to confirm, so the selection and the draft are not sent.
 */
export const buildReleaseBody = ({
    motif,
    selectedSlots,
    serverOpenSlots,
    draftOpenSlots,
    oneOffLiveIds = [],
}) => {
    if (motif === RELEASE_MOTIF.UNAVAILABLE) {
        return {
            slots: [],
            all: true,
            motif,
            new_slots: [],
            dropped_slots: [],
        }
    }

    return {
        slots: selectedSlots.map(slotKeyOf),
        all: false,
        motif,
        ...weekDiff(serverOpenSlots, draftOpenSlots ?? serverOpenSlots),
        // The trial or stage the flow was opened from: the server reads its hour off the live.
        ...(oneOffLiveIds.length ? { live_session_ids: oneOffLiveIds } : {}),
    }
}

/** The lives a « toutes les semaines » from a live opens the flow on, without a créneau of the week to mark. */
export const ONE_OFF_ROOM_TYPES = ["trial", "holiday_course"]

/** How the week step names the trial or stage it was opened from: « essai du mardi à 17h ». */
export const oneOffLabel = (roomType, dayTime) =>
    `${roomType === "trial" ? "essai" : "stage"} du ${dayTimeLabel(dayTime)}`

/** The card of a trial's student on an hour he gives up: another tutor takes it, the family is not told. */
export const trialCard = (trial) => ({
    key: `t-${trial.live_session_id}`,
    name: trial.student_name ?? "Essai",
    tag: "essai perdu",
    tone: "lost",
    lines: [`Son essai du ${dayDateTimeLabel(trial.date)} passe à un autre tuteur.`],
})

const newSlotSince = (student) =>
    `${student.new_slot ? dayTimeLabel(student.new_slot) : ""}${
        student.effective_on
            ? ` dès le ${shortDateLabel(student.effective_on)}`
            : ""
    }`.trim()

/** "Tuesday 18:00", "Tuesday 19:00" → « Son cours du mardi à 18h » / « Ses cours du mardi à 18h et du mardi à 19h ». */
export const coursesLabel = (slots = []) =>
    slots.length > 1
        ? `Ses cours du ${slots.map(dayTimeLabel).join(" et du ")}`
        : `Son cours du ${dayTimeLabel(slots[0] ?? "")}`


/**
 * One student of the preview → its card: its tag, its lines in reading order,
 * and the créneaux the tutor could open to keep him.
 * The tag is the tutor's side of the story: a reattributed student whose family
 * can still pick one of his créneaux is not lost yet (« risque de perte »); one
 * whose family has nothing left to pick is « Élève perdu ». « Nouveau tuteur » is
 * the family's side, never written here. An at-risk card leads with its `alert`.
 * @returns {{tag: string, staysWithTutor: boolean, atRisk: boolean, lost: boolean, alert: ?string, lines: string[], suggestion: ?string, suggestedSlots: string[]}}
 */
export const studentRow = (student) => {
    const lines = []
    const staysWithTutor = student.outcome !== STUDENT_OUTCOME.REATTRIBUTION
    const atRisk = !staysWithTutor && !!student.choice_deadline
    const lost = !staysWithTutor && !atRisk
    const courses = student.freed?.length ? coursesLabel(student.freed) : null
    const several = (student.freed?.length ?? 0) > 1

    if ((student.stays?.length ?? 0) > 1) {
        // Several freed courses stay with him: one line per course, each on its own créneau.
        for (const stay of student.stays) {
            const since = newSlotSince({ new_slot: stay.new_slot, effective_on: stay.effective_on })
            lines.push(
                stay.new_group
                    ? `${coursesLabel([stay.freed])} passe dans un nouveau groupe, le ${since}`
                    : `${coursesLabel([stay.freed])} passe au ${since}`
            )
        }
    } else if (student.outcome === STUDENT_OUTCOME.KEPT) {
        lines.push(courses ? `${courses} passe au ${newSlotSince(student)}` : newSlotSince(student))
    } else if (student.outcome === STUDENT_OUTCOME.NEW_GROUP) {
        lines.push(
            courses
                ? `${courses} passe dans un nouveau groupe, le ${newSlotSince(student)}`
                : `Nouveau groupe ${newSlotSince(student)}`
        )
    } else {
        const pause = student.paused_until ? "En pause. " : ""
        if (atRisk) {
            const passes = several ? "ils passent" : "il passe"
            lines.push(`${pause}${courses ?? "Ce cours"} : sans réponse avant le ${dayDateTimeLabel(student.choice_deadline)}, ${passes} à un autre tuteur.`)
        } else {
            if (courses) lines.push(`${courses} chez toi ${several ? "s'arrêtent" : "s'arrête"}.`)
            if (student.enters_cohort === "now") {
                lines.push(`${pause}${several ? "Tu perds ces cours" : "Tu perds ce cours"} : tu n'as pas assez de créneaux libres à proposer à sa famille.`)
            } else if (pause) {
                lines.push(pause.trim())
            }
        }
        // The block leaves with the freed course, unless the family stays with the tutor.
        for (const slot of student.leaving ?? []) {
            lines.push(
                atRisk
                    ? `Si sa famille change de tuteur, son cours du ${dayTimeLabel(slot)} part aussi.`
                    : `Son cours du ${dayTimeLabel(slot)} chez toi s'arrête aussi.`
            )
        }
        if (student.split_leaving?.length) {
            lines.push(
                `Sa famille peut aussi te garder pour ${coursesLabel(student.split_leaving).replace(/^S/, "s")} seulement.`
            )
        }
    }

    for (const date of student.covered_dates ?? []) {
        lines.push(`${dayDateLabel(date)} : un remplaçant est demandé.`)
    }

    const suggestedSlots = staysWithTutor ? [] : (student.suggested_slots ?? [])

    return {
        tag: staysWithTutor ? "reste avec toi" : atRisk ? "risque de perte" : "Élève perdu",
        staysWithTutor,
        atRisk,
        lost,
        alert: atRisk ? "Nous proposons tes créneaux à sa famille : elle peut les refuser et changer de tuteur." : null,
        lines: lines.filter(Boolean),
        suggestion: suggestedSlots.length
            ? `${student.student_firstname || student.student_name || "Cet élève"} est aussi disponible le ${suggestedSlots.map(dayTimeLabel).join(", le ")} : ${suggestedSlots.length > 1 ? "ouvre ces créneaux" : "ouvre ce créneau"} pour le garder.`
            : null,
        suggestedSlots,
    }
}

const severityOf = (row) => (row.lost ? 0 : row.atRisk ? 1 : 2)

/** The students of the preview with their card, the most serious first: lost, at risk, then those who stay. */
export const studentRowsBySeverity = (students = []) =>
    students
        .map((student) => ({ student, row: studentRow(student) }))
        .sort((a, b) => severityOf(a.row) - severityOf(b.row))

/**
 * How many of his students the preview may cost him: those who change tutor unless their family
 * keeps him (once each, whatever their cards), and those of a stage his cards call lost. A trial's
 * student is not his yet, so a lost trial raises no warning.
 */
export const studentsAtRiskCount = (preview) => {
    const students = new Set(
        (preview?.students ?? [])
            .filter((student) => student.outcome === STUDENT_OUTCOME.REATTRIBUTION)
            .map((student) => String(student.student_id))
    )
    const stageStudents = (preview?.stages ?? []).flatMap(stageCards).filter((card) => card.tone === "lost").length

    return students.size + stageStudents
}

export const rattrapageLine = (rattrapage) =>
    `${dayDateTimeLabel(rattrapage.date)}. Son crédit est gardé.`

/**
 * What happens to a stage, decided by the server on the hours he has given it, said on each of its
 * students' cards: the rest of its week goes to another tutor, or a substitute gives only the sessions
 * he can't, and he keeps the others.
 */
export const stageLine = (stage) => {
    const dates = stage.dates ?? []
    if (stage.outcome === STAGE_OUTCOME.REATTRIBUTED) {
        const span = dates.length > 1 ? `La suite de son stage, du ${dayDateLabel(dates[0])} au ${dayDateLabel(dates[dates.length - 1])},` : `Son stage du ${dayDateLabel(dates[0])}`
        return `${span} passe à un autre tuteur, pour garder le même tuteur jusqu'au bout.`
    }
    const sessions = dates.length > 1 ? "pour les séances du" : "pour la séance du"
    return `Son stage du ${dayTimeLabel(stage.day_time)} continue sans toi : un remplaçant est demandé ${sessions} ${dates.map(dayDateLabel).join(", le ")}.${stage.keeps_rest ? " Tu gardes le reste du stage." : ""}`
}

/** One card per student of a stage on an hour he gives up. */
export const stageCards = (stage) => {
    const reattributed = stage.outcome === STAGE_OUTCOME.REATTRIBUTED
    const tag = reattributed ? "stage perdu" : stage.keeps_rest ? "reste avec toi" : "remplaçant"
    const tone = reattributed ? "lost" : stage.keeps_rest ? "stays" : "atRisk"
    const names = stage.student_names?.length ? stage.student_names : ["Stage"]

    return names.map((name, index) => ({ key: `st-${stage.live_session_id}-${index}`, name, tag, tone, lines: [stageLine(stage)] }))
}

/** A course that left with its block while the family chooses, on an hour he gives up too: it won't come back to him. */
export const keptSeatLine = (kept) =>
    `Son cours ${kept.subjects ? `de ${kept.subjects} ` : ""}du ${dayTimeLabel(kept.day_time)} est déjà parti avec son bloc : il ne reviendra pas avec toi, même si sa famille te garde pour ses autres cours.`

/** A live on a freed créneau that the release leaves alone (permanence…). */
export const untouchedLiveLine = (live) => {
    const when = dayTimeLabel(live.day_time)
    switch (live.room_type) {
        case "support":
            return `Ta permanence du ${when} n'est pas concernée : elle reste prévue.`
        case "holiday_workbook":
            return `Ton cours d'été du ${when} n'est pas concerné : il reste prévu.`
        default:
            return `Ton cours du ${when} n'est pas concerné : il reste prévu.`
    }
}
